/**
 * I passi del tutorial guidato dell'elenco Fattureincloud Ordini.
 *
 * Il menu di riga ha una voce sola e cambia a seconda che il documento sia gia'
 * stato sincronizzato o no. Per farle vedere tutte e due, ogni passo apre il menu
 * della prima riga **del tipo che gli serve**: le righe portano un marcatore che
 * dice di che tipo sono.
 *
 * I bersagli della pagina sono scoped; le voci del menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="fic-orders"] [data-tour="${name}"]`;

const FIC_SYNC = '[data-tour="fic-sync"]';
const FIC_DETAIL = '[data-tour="fic-detail"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * Il menu si chiude al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

/**
 * Apre il menu della prima riga del tipo richiesto.
 *
 * `kind` e' 'row-new' per un documento ancora da sincronizzare, 'row-synced' per
 * uno gia' fatto. Se righe di quel tipo non ce ne sono, non si aspetta: il passo
 * si aggancia in basso e spiega comunque a parole.
 */
async function openRowMenu(kind, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const activator = document.querySelector(`${at(kind)} [data-tour="row-menu"]`);

	if (!activator) {
		return;
	}

	activator.click();
	await waitFor(probe, true);
}

async function closeRowMenu() {
	await afterPendingClose();

	for (const [kind, probe] of [
		['row-new', FIC_SYNC],
		['row-synced', FIC_DETAIL],
	]) {
		if (isVisible(probe)) {
			const activator = document.querySelector(`${at(kind)} [data-tour="row-menu"]`);

			// L'attivatore fa da interruttore: il secondo click richiude.
			if (activator) {
				activator.click();
				await waitFor(probe, false);
			}
		}
	}
}

const openNewRow = () => openRowMenu('row-new', FIC_SYNC);
const openSyncedRow = () => openRowMenu('row-synced', FIC_DETAIL);

/** Richiude il menu a fine giro. */
export function stopFicOrdersTour() {
	return closeRowMenu();
}

export function createFicOrdersTourSteps() {
	return [
		{
			body: "Questa pagina legge i documenti dal tuo Fattureincloud e li mette accanto al catalogo di Azseller. Serve a una cosa sola, ma importante: far entrare in magazzino la merce che hai gia' fatturato nel gestionale, senza doverla reinserire a mano. Se la tabella non compare affatto, vuol dire che non e' arrivato nessun documento - o che il collegamento a Fattureincloud non e' attivo.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Le colonne arrivano tali e quali da Fattureincloud: la data del documento, il suo Id, l'anagrafica intestataria, la descrizione e l'importo. La cosa da guardare per prima pero' e' il colore: le righe verdi sono i documenti gia' sincronizzati in Azseller, le altre no. E' l'unico indicatore di stato che ha questa pagina.",
			before: closeRowMenu,
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: 'I tre pallini a inizio riga aprono una voce sola, e cambia a seconda di dove sei: sulle righe non ancora sincronizzate propone di sincronizzare, su quelle verdi mostra il dettaglio di cosa era stato fatto. Le vediamo tutte e due.',
			before: closeRowMenu,
			key: 'row-menu',
			placement: 'right',
			target: at('row-menu'),
			timeout: 250,
			title: 'Il menu della riga',
		},
		{
			body: "Sincronizza ordine apre la scheda dove si abbinano le righe del documento ai prodotti del catalogo: si spunta cosa far entrare, si collega ogni riga a una variante con la lente, e si correggono prezzo, quantita' e IVA se serve. Al salvataggio succedono tre cose: le quantita' si sommano alla giacenza, il prezzo unitario diventa il nuovo prezzo d'acquisto della variante, e il fornitore viene collegato al prodotto - ma solo se in anagrafica ne esiste uno con la stessa ragione sociale scritta sul documento.",
			before: openNewRow,
			key: 'fic-sync',
			placement: 'right',
			target: FIC_SYNC,
			timeout: 250,
			title: 'Sincronizzare un documento',
		},
		{
			body: "Finita la sincronizzazione la riga diventa verde e questa voce prende il posto dell'altra: mostra in sola lettura le righe che erano state abbinate. E' anche la garanzia che non si sincronizzi due volte lo stesso documento - cosa che raddoppierebbe le quantita' caricate - perche' su una riga verde la voce Sincronizza semplicemente non c'e' piu'.",
			before: openSyncedRow,
			key: 'fic-detail',
			placement: 'right',
			target: FIC_DETAIL,
			timeout: 250,
			title: 'Il dettaglio di una sincronizzazione',
		},
	];
}
